import React, { useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, Paper, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { GroupsForm } from "components/Measure-data/Groups-form/GroupsForm";
import {
  Group,
  MeasureData,
  MeasureDataGroup,
  MeasureDataRound
} from "components/Measure-data/Groups-form/GroupsFormContext";
import { DEBT_MEASURE_FETCHABLE_NAME, DEBT_MEASURE_ROLES } from "constants/debt";
import { uniq } from "lodash";
import {
  useDealDefaultCurrencySelect,
  useDealIdSelector,
  useDealSubAssetsSelector,
  useParentDealSelect
} from "store/selectors/deal-selectors/deal-selectors";
import { MeasureDataDraft } from "store/types";
import { getFacilitySegment } from "utils/debt";

import { useMeasureDrawer } from "../../hooks/debt-hooks/use-measure-drawer";
import { FormValues } from "./schemas";

interface FacilityTermsDrawerValuesProps {
  open: boolean;
  rounds: Record<number, MeasureDataRound[]>;
  loading: boolean;
  onClose: () => void;
  segments: FormValues[];
  onSave: (data: { groups: MeasureDataGroup[]; roundsByDeals: Record<number, MeasureDataRound[]> }) => void;
  onSaveAsDraft: (data: MeasureDataDraft["data"]) => Promise<void>;
  onSkip?: () => void;
}

export const FacilityTermsDrawerValues: React.FC<FacilityTermsDrawerValuesProps> = ({
  open,
  rounds,
  loading,
  onClose,
  segments,
  onSave,
  onSaveAsDraft,
  onSkip
}) => {
  const contextDealId = useDealIdSelector();
  const dealIds = uniq(segments.map((segment) => segment.deal_id));
  const facilitySegment = getFacilitySegment(segments);

  const { addMeasureToForm, getMeasuresOfFetchable, commitmentMeasure } = useMeasureDrawer(dealIds);
  const debtMeasures = getMeasuresOfFetchable(DEBT_MEASURE_FETCHABLE_NAME);
  const currency = useDealDefaultCurrencySelect() || "USD";

  const disbursementMeasure = debtMeasures.find((param) => param.roles[0] === DEBT_MEASURE_ROLES.disbursement);

  const subDeals = useDealSubAssetsSelector();
  const parent = useParentDealSelect();

  const initialData = useMemo(() => {
    const measureData: MeasureData[] = [];
    const groups: Group[] = [];

    if (rounds) {
      for (const dealId of dealIds) {
        const roundsForDeal = rounds[dealId];
        const segment = segments.find((segment) => segment.deal_id === dealId)!;
        if (roundsForDeal?.length) {
          const firstRound = roundsForDeal[0];
          const addMeasure = addMeasureToForm(firstRound, measureData, groups);

          if (segment.is_facility && commitmentMeasure) {
            addMeasure({
              value: segment.loan_amount,
              date: segment.start_date,
              subDealId: dealId,
              measureId: commitmentMeasure.id
            });
          }

          if (segment.is_withdrawal && disbursementMeasure) {
            addMeasure({
              value: segment.loan_amount,
              date: segment.start_date,
              subDealId: dealId,
              measureId: disbursementMeasure.id
            });
          }
        }
      }
    }

    return { measureData, groups };
  }, [rounds, segments, commitmentMeasure, addMeasureToForm]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Paper
        sx={{
          width: "90vw",
          maxWidth: "1200px",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: "20px"
        }}
      >
        <Box
          sx={{
            p: 5,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Typography variant="h5">Add Commitment</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "column",
            overflow: "auto"
          }}
        >
          <AsyncContentMui isLoading={loading}>
            {rounds && (
              <GroupsForm
                deal_id={facilitySegment?.deal_id || contextDealId || 0}
                subDeals={subDeals}
                parentId={parent?.id || null}
                initialRoundsByDeal={rounds}
                initialGroups={initialData.groups}
                initialMeasureData={initialData.measureData}
                onSave={onSave}
                onSaveAsDraft={onSaveAsDraft}
                onClose={onClose}
                onSkip={onSkip}
                disableDealSelect
                disableSubDealSelect
                defaultCurrency={currency}
              />
            )}
          </AsyncContentMui>
        </Box>
      </Paper>
    </Modal>
  );
};
